<template>
  <SpinnerComp v-if="loading" />
  <div
    v-else
    class="p-6 bg-slate-50 min-h-screen mt-8 mb-8 ml-2 mr-2 rounded-xl shadow-2xl flex flex-col"
  >
    <div class="text-center mb-6">
      <p class="text-lg text-gray-600">Hoy es</p>
      <h1 class="text-3xl font-bold text-gray-800">{{ DateTime }}</h1>
    </div>

    <div class="grid grid-cols-2 sm:grid-cols-3 gap-6 mb-6">
      <div
        class="p-6 bg-white shadow rounded flex flex-col justify-between h-full items-start space-y-2"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-8 w-8 stroke-gray-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line x1="8" y1="6" x2="21" y2="6" />
          <line x1="8" y1="12" x2="21" y2="12" />
          <line x1="8" y1="18" x2="21" y2="18" />
          <circle cx="3" cy="6" r="1" />
          <circle cx="3" cy="12" r="1" />
          <circle cx="3" cy="18" r="1" />
        </svg>
        <p class="text-gray-600 font-semibold text-sm uppercase tracking-wider">
          Notas Pendientes
        </p>
        <p class="text-3xl font-bold text-right w-full">{{ notasPendiente }}</p>
      </div>

      <div
        class="p-6 bg-white shadow rounded flex flex-col justify-between h-full items-start space-y-2"
      >
        <!-- Icono minimalista: calendario -->
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-8 w-8 stroke-blue-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <p class="text-gray-600 font-semibold text-sm uppercase tracking-wider">
          Vencen Hoy
        </p>
        <p class="text-3xl font-bold text-right w-full">{{ vencenHoy }}</p>
      </div>

      <div
        class="p-6 bg-white shadow rounded flex flex-col justify-between h-full items-start space-y-2"
      >
        <!-- Icono minimalista: alerta / warning -->
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-8 w-8 stroke-red-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
          />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        <p class="text-gray-600 font-semibold text-sm uppercase tracking-wider">
          Vencidas
        </p>
        <p class="text-3xl font-bold text-right w-full">{{ vencidas }}</p>
      </div>
    </div>

    <div class="flex-grow overflow-auto space-y-3 ">
      <div
        v-for="nota in paginatedNotas"
        :key="nota.id"
        :class="[
          'p-4 shadow rounded-lg flex items-center justify-between',
          nota.claseVencimiento,
        ]"
      >
        <div>
          <h2 class="text-lg font-bold text-gray-800">{{ nota.asunto }}</h2>
          <p class="text-gray-600">{{ nota.descripcion }}</p>
          <p class="text-sm text-gray-500">
            Vence el:
            <span class="font-semibold">{{
              formatDate(nota.vencimiento)
            }}</span>
          </p>
        </div>
        <div>
          <span :class="[nota.clasePrioridad, 'px-3 py-1 rounded text-sm']">
            {{ nota.prioridad }}
          </span>
        </div>
      </div>
    </div>

    <footer
      class="mt-6 flex justify-center items-center space-x-3 p-3"
    >
      <button
        class="px-6 py-3 bg-blue-600 text-white rounded-full shadow-md hover:bg-blue-700 active:scale-95 transition-transform disabled:opacity-40 disabled:cursor-not-allowed"
        @click="currentPage--"
        :disabled="currentPage === 1"
      >
        ← Anterior
      </button>
      <p class="text-gray-700 font-semibold select-none">
        Página {{ currentPage }} de {{ totalPages }}
      </p>
      <button
        class="px-6 py-3 bg-blue-600 text-white rounded-full shadow-md hover:bg-blue-700 active:scale-95 transition-transform disabled:opacity-40 disabled:cursor-not-allowed"
        @click="currentPage++"
        :disabled="currentPage === totalPages"
      >
        Siguiente →
      </button>
    </footer>
  </div>
</template>

<script>
import SpinnerComp from "@/components/SpinnerComp.vue";

export default {
  data() {
    return {
      DateTime: "",
      currentPage: 1,
      itemsPerPage: 4,
      loading: false,
      notas: [],

      notasPendiente: 0,
      vencenHoy: 0,
      vencidas: 0,
    };
  },
  components: {
    SpinnerComp,
  },
  computed: {
    totalPages() {
      return Math.ceil(this.notas.length / this.itemsPerPage);
    },
    paginatedNotas() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.notas.slice(start, start + this.itemsPerPage);
    },
  },
  methods: {
    updateDateTime() {
      const now = new Date();
      this.DateTime = now.toLocaleString("es-MX", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    },
    formatDate(date) {
      const newDate = new Date(date + "T00:00:00");
      return newDate.toLocaleDateString("es-MX", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    },
    getData() {
      this.loading = true;
      this.$axios
        .get("/getNotesPending")
        .then((response) => {
          this.notas = response.data.notas.map((item) => ({
            id: item.id,
            asunto: item.asunto,
            descripcion: item.descripcion,
            vencimiento: item.fechaVence,
            prioridad: item.prioridad,
            clasePrioridad: item.clasePrioridad,
            claseVencimiento: item.claseVencimiento,
          }));
          console.log(this.notas);
          this.notasPendiente = response.data.estadisticas?.notasPendiente || 0;
          this.vencenHoy = response.data.estadisticas?.vencenhoy || 0;
          this.vencidas = response.data.estadisticas?.vencidas || 0;
        })
        .catch((error) => {
          alert(error.response?.data?.message || "Error al cargar datos");
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },

  mounted() {
    setInterval(() => {
      this.updateDateTime();
    }, 1000);
    this.getData();
  },
};
</script>
